import { useEffect, useRef, useState } from 'react';
import { AUTO_TARGETS, multiplierAt, type CrashState } from '../../engine/games/crash';
import { money } from '../../format';
import { BetControl } from '../parts';
import { BigBoom, Controls, Stage, StatusLine, type TableProps } from './Frame';

const fmt = (m: number) => `${m.toFixed(2)}×`;

/** The multiplier right now, redrawn every frame while the rocket flies (the
 *  engine resolves crashes and auto cash-outs on its own tick). */
function useLiveMultiplier(state: CrashState | undefined): number {
  const flying = state?.phase === 'flying';
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!flying) return;
    // A plain timer rather than requestAnimationFrame, which stops whenever
    // the page isn't being drawn and would freeze the number mid-flight.
    const t = setInterval(() => setNow(Date.now()), 50);
    return () => clearInterval(t);
  }, [flying]);
  if (!state) return 1;
  if (!flying) return state.phase === 'done' ? state.shown : 1;
  return Math.max(1, Math.min(multiplierAt(now - state.startedAt), state.crashPoint));
}

/** Stars at fixed spots across the sky, each with a depth (nearer stars
 *  streak faster). Seeded so the sky is the same every visit. */
const STARS = (() => {
  let seed = 7;
  const r = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  return Array.from({ length: 42 }, () => ({ x: r() * 320, y: r() * 200, depth: 0.4 + r() * 1.1, size: 0.6 + r() * 1.1 }));
})();

/** The flight: a rocket climbing a night sky, its exhaust trail bending
 *  upward like the real multiplier curve. Rescales so the tip stays in view,
 *  and the stars streak past faster the higher it goes. */
function Chart({ top, crashed, cashedAt, auto, flying }: { top: number; crashed: boolean; cashedAt: number | null; auto: number | null; flying: boolean }) {
  const W = 320;
  const H = 200;
  const pad = 14;
  const ground = H - 10;
  const elapsed = Math.log(Math.max(1, top)) / (Math.LN2 / 6000);
  const span = Math.max(4000, elapsed * 1.08);
  const maxM = Math.max(2, top * 1.18, auto && auto < top * 2 ? auto * 1.1 : 0);
  const x = (t: number) => pad + 10 + (t / span) * (W - pad * 2 - 10);
  const y = (m: number) => ground - 8 - ((m - 1) / (maxM - 1)) * (ground - 8 - pad);
  const steps = 48;
  const pts: string[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (elapsed * i) / steps;
    pts.push(`${x(t).toFixed(1)},${y(Math.exp((Math.LN2 / 6000) * t)).toFixed(1)}`);
  }
  const tipX = x(elapsed);
  const tipY = y(top);
  // The rocket points along the curve.
  const dt = Math.max(60, elapsed * 0.04);
  const angle = (Math.atan2(y(Math.exp((Math.LN2 / 6000) * (elapsed + dt))) - tipY, x(elapsed + dt) - tipX) * 180) / Math.PI + 90;
  const gridLines = [1.5, 2, 3, 5, 10, 25, 50, 100, 250].filter((m) => m < maxM).slice(-3);
  const cashT = cashedAt ? Math.log(cashedAt) / (Math.LN2 / 6000) : null;
  // Stars drift down as the rocket climbs, faster (and longer streaks) with height.
  const drift = elapsed * 0.012 + (top - 1) * 38;
  const streak = flying ? Math.min(26, (top - 1) * 5) : 0;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="crash-chart" role="img" aria-label={crashed ? `Crashed at ${fmt(top)}` : `At ${fmt(top)}`}>
      <defs>
        <linearGradient id="crash-trail" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#ff6a2b" stopOpacity="0.15" />
          <stop offset="1" stopColor="#ffd84a" />
        </linearGradient>
        <radialGradient id="crash-moon" cx="0.4" cy="0.4" r="0.6">
          <stop offset="0" stopColor="#fff7df" />
          <stop offset="1" stopColor="#cfc4a4" />
        </radialGradient>
      </defs>
      <circle cx={W - 42} cy={34} r={15} fill="url(#crash-moon)" className="moon" />
      <circle cx={W - 47} cy={30} r={3} className="crater" />
      <circle cx={W - 37} cy={40} r={2} className="crater" />
      {STARS.map((st, i) => {
        const sy = ((st.y + drift * st.depth) % H + H) % H;
        const len = streak * st.depth;
        return len > 1.5 ? (
          <line key={i} x1={st.x} x2={st.x} y1={sy - len} y2={sy} className="star-streak" strokeWidth={st.size * 0.8} />
        ) : (
          <circle key={i} cx={st.x} cy={sy} r={st.size * 0.7} className="star" />
        );
      })}
      {gridLines.map((m) => (
        <g key={m}>
          <line x1={pad} x2={W - pad} y1={y(m)} y2={y(m)} className="grid" />
          <text x={W - pad} y={y(m) - 4} textAnchor="end" className="grid-label">{fmt(m)}</text>
        </g>
      ))}
      {auto && auto < maxM && <line x1={pad} x2={W - pad} y1={y(auto)} y2={y(auto)} className="auto-line" />}
      <path d={`M0 ${ground} Q40 ${ground - 7} 80 ${ground - 2} T170 ${ground - 3} T260 ${ground - 1} T320 ${ground - 4} V${H} H0 Z`} className="horizon" />
      <rect x={pad + 3} y={ground - 4} width="14" height="4" className="pad" />
      <polyline points={pts.join(" ")} className={`trail${crashed ? " crashed" : ""}`} />
      <polyline points={pts.join(" ")} className={`curve${crashed ? " crashed" : ""}`} />
      {cashT !== null && <circle cx={x(cashT)} cy={y(cashedAt!)} r="7" className="cash-dot" />}
      {crashed ? (
        <g transform={`translate(${tipX} ${tipY})`} className="boom">
          <polygon points="0,-16 5,-6 16,-8 8,1 14,12 3,7 -4,16 -6,6 -16,6 -8,-2 -14,-12 -3,-7" />
        </g>
      ) : (
        <g transform={`translate(${tipX} ${tipY}) rotate(${flying ? angle : 0})`} className={`rocket${flying ? " lit" : ""}`}>
          <path d="M-4,10 L-9,16 L-4,14 Z M4,10 L9,16 L4,14 Z" className="fin" />
          <path d="M0,-14 C6,-7 6,6 4,12 L-4,12 C-6,6 -6,-7 0,-14 Z" className="hull" />
          <path d="M0,-14 C3,-11 4,-8 4.5,-6 L-4.5,-6 C-4,-8 -3,-11 0,-14 Z" className="nose" />
          <circle cx="0" cy="-1" r="2.4" className="window" />
          <path d="M-3,12 L0,22 L3,12 Z" className="flame" />
        </g>
      )}
    </svg>
  );
}

export function CrashTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.crash?.state as CrashState | undefined;
  const live = useLiveMultiplier(state);
  const phase = state?.phase ?? 'idle';
  const auto = state ? AUTO_TARGETS[state.autoIndex] ?? null : null;
  const top = phase === 'done' ? state!.crashPoint : live;
  const crashed = phase === 'done' && state!.cashedAt === null;
  const cashing = phase === 'flying' ? Math.round(state!.bet * live) : 0;
  // A crash fills the screen with a BOOM (user, 2026-09-27), only when it
  // happens in front of you: flying, then crashed. Reopening a table that
  // already crashed doesn't replay it.
  const wasFlying = useRef(false);
  const [boomAt, setBoomAt] = useState<number | null>(null);
  useEffect(() => {
    if (phase === 'flying') wasFlying.current = true;
    else if (wasFlying.current) {
      wasFlying.current = false;
      if (crashed) setBoomAt(state!.endedAt);
    }
  }, [phase, crashed, state]);

  let result: string | undefined;
  let tone: 'win' | 'loss' | '' = '';
  if (phase === 'done' && state) {
    const net = state.returned - state.bet;
    result = state.cashedAt === null ? `Crashed at ${fmt(state.crashPoint)} (−${money(state.bet)})` : `Out at ${fmt(state.cashedAt)}: ${net >= 0 ? '+' : '−'}${money(Math.abs(net))} · it crashed at ${fmt(state.crashPoint)}`;
    tone = net > 0 ? 'win' : net < 0 ? 'loss' : '';
  }

  return (
    <>
      <BigBoom trigger={boomAt} />
      <Stage className="crash-stage">
        <div className={`crash-readout${crashed ? ' crashed' : phase === 'done' ? ' cashed' : ''}`}>
          <span className="crash-mult">{fmt(phase === 'idle' ? 1 : phase === 'done' && !crashed ? state!.cashedAt! : top)}</span>
          <span className="crash-sub">
            {phase === 'flying'
              ? `Worth ${money(cashing)}${auto ? ` · auto at ${fmt(auto)}` : ''}`
              : phase === 'idle'
                ? 'Launch, then cash out before it crashes.'
                : crashed
                  ? 'Crashed'
                  : `Cashed out · it went on to ${fmt(state!.crashPoint)}`}
          </span>
        </div>
        <Chart top={phase === 'idle' ? 1 : top} crashed={crashed} cashedAt={phase === 'done' ? state!.cashedAt : null} auto={auto} flying={phase === 'flying'} />
        {state && state.history.length > 0 && (
          <div className="history" aria-label="Last crash points">
            {state.history.map((m, i) => <span key={i} className={m >= 2 ? 'hi' : 'lo'}>{fmt(m)}</span>)}
          </div>
        )}
        <StatusLine snapshot={snapshot} result={result} tone={tone} />
      </Stage>
      {phase === 'flying' ? (
        // Same shape as the usual controls (the bet row, locked, then one
        // button), so nothing jumps when the rocket launches.
        <footer className="controls">
          <BetControl bet={run.tables.crash?.bet ?? 0} ladder={[]} onChange={() => {}} locked />
          <div className="control-buttons" style={{ ['--count' as string]: 1 }}>
            <button className="btn btn-primary" onClick={() => actions.tableOption('cashout')}>Cash out {money(cashing)}</button>
          </div>
        </footer>
      ) : (
        <Controls
          snapshot={snapshot}
          actions={actions}
          hide={['auto']}
          extra={[{ id: 'auto-cycle', label: `Auto ${auto === null ? 'off' : fmt(auto)}`, onClick: () => actions.tableOption('auto') }]}
        />
      )}
    </>
  );
}
